import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ApiService, ViewTypeEnum, Request, FormTypeEnum } from "../../api";
import { Button, Col, Collapse, Row, Space, Steps, Tag, message } from "antd";
import { StepProps } from "antd/lib";
import { CreateStepType, openNotificationWithIcon } from "../../constants";
import WorkflowViewPage from "./WorkflowViewPage";
import RenderFormComponent from "../../components/RenderFormComponent";
import { CaretRightOutlined } from "@ant-design/icons";

export type CreateStepsProps = {
  form_id?: number;
  schema?: any;
  form?: any;
  workflow_id?: number;
  type: CreateStepType;
  formType?: FormTypeEnum;
} & StepProps;

const EditRequestPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();

  const location = useNavigate();
  const [current, setCurrent] = useState<number>(0);
  const [viewType, setViewType] = useState<ViewTypeEnum>(ViewTypeEnum.COLLAPSE);
  const [items, setItems] = useState<CreateStepsProps[]>([]);
  const [request, setRequest] = useState<Request | undefined>(undefined);

  const loadRequest = async () => {
    const response = await ApiService.apiMainRequestsRetrieve(parseInt(id!));
    setRequest(response);
  };

  useEffect(() => {
    loadRequest();
  }, [id]);

  const prev = () => {
    setCurrent(current - 1);
  };

  const handleSuccessSubmit = async (result: any) => {
    if (current === items.length - 1) {
      openNotificationWithIcon("success", t("Form successfully saved"));
      await ApiService.apiMainGenerateDocumentRetrieve(parseInt(id!));
      location("/requests/" + parseInt(id!));
    } else {
      setCurrent(current + 1);
    }
  };

  useEffect(() => {
    (async () => {
      await ApiService.apiMainRequestsRetrieve(parseInt(id!)).then((result) => {
        setViewType(result.template.view_type ?? ViewTypeEnum.COLLAPSE);
        const items: CreateStepsProps[] = result.template.form_templates.map(
          (ft, index) => {
            return {
              key: index,
              title: t(ft.title),
              form_id: ft.id,
              form: ft.schema_json.form,
              schema: ft.schema_json.schema,
              type: CreateStepType.Form,
              formType: ft.type,
            };
          }
        );
        console.log(items);

        const workflows: CreateStepsProps[] = result.template.workflows.map(
          (ft, index) => {
            return {
              key: index,
              title: t(ft.name),
              workflow_id: ft.id,
              type: CreateStepType.Workflow,
            };
          }
        );

        setItems([...items, ...workflows]);
      });
    })();
  }, []);

  const renderView = () => {
    if (items.length === 0) {
      return null;
    }

    if (items[current].type === CreateStepType.Form) {
      return (
        <RenderFormComponent
          form={items[current]}
          requestId={parseInt(id!)}
          onSuccess={handleSuccessSubmit}
        />
      );
    }

    return (
      <WorkflowViewPage
        requestId={parseInt(id!)}
        workflowId={items[current].workflow_id}
        onSuccess={handleSuccessSubmit}
      />
    );
  };

  if (viewType === ViewTypeEnum.STEP) {
    return (
      <div>
        <h2>
          {" "}
          {t("Edit")}: {request?.number} | <Tag>{request?.status?.name}</Tag>
        </h2>
        <Steps current={current} items={items} onChange={setCurrent} />
        <div style={{ marginTop: 24 }}>{renderView()}</div>
        <div style={{ marginTop: 24 }}>
          {current > 0 && (
            <Button style={{ margin: "0 8px" }} onClick={() => prev()}>
              {t("Previous")}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <Row justify="space-between" align="middle">
        <Col>
          <h2>
            {t("Edit")}: {request?.number} | <Tag>{request?.status?.name}</Tag>
          </h2>
        </Col>
        <Col>
          <Button
            style={{ marginLeft: "0px" }}
            type="primary"
            onClick={() => location(`/requests/${id}`)}
          >
            {t("Return Back")}
          </Button>
        </Col>
      </Row>

      <Space direction="vertical" style={{ width: "100%" }}>
        {items.map((page) => (
          <Collapse
            expandIcon={({ isActive }) => (
              <CaretRightOutlined rotate={isActive ? 90 : 0} />
            )}
            ghost={true}
            items={[
              {
                key: page.form_id,
                label: page.title,
                children:
                  page.type === CreateStepType.Form ? (
                    <RenderFormComponent
                      form={page}
                      requestId={parseInt(id!)}
                      onSuccess={handleSuccessSubmit}
                    />
                  ) : (
                    <WorkflowViewPage
                      requestId={parseInt(id!)}
                      workflowId={page.workflow_id}
                      onSuccess={handleSuccessSubmit}
                    />
                  ),
              },
            ]}
          />
        ))}
      </Space>
    </div>
  );
};

export default EditRequestPage;
